/* eslint-disable react/no-access-state-in-setstate -- Rule crashes on this class component under ESLint 7. */
/**
 * @typedef {import('../../../types/AuthContext').default} Auth
 * @typedef {import('../../Agent/Contacts/ContactProps').Contact} Contact
 * @typedef {import('./ContactFormProps').default['initialValues']} ContactFormValues
 * @typedef {import('./EditContactProps').default} Props
 * @typedef {import('./EditContactState').default} State
 * @typedef {{ amountToLoan: number | null, amortizationYears: number | null, escrowInsuranceAmount: number | null, escrowInsuranceFrequency: 'annual' | 'monthly', escrowInsuranceMonthly: number | null, escrowTaxAmount: number | null, escrowTaxFrequency: 'annual' | 'monthly', escrowTaxMonthly: number | null, originalDateOfContract: Date | null, rate: number | null }} MortgageDetails
 */

import { AuthContext } from '../../../shared/AuthProvider';
import { ContactDeletePopup } from '../shared/ContactDeletePopup';
import {
	assignContactTags,
	createContactActivity,
	createContactTag,
	getAgentId,
	getContact,
	getContactActivity,
	getContactHomeownerProperties,
	getContactTags,
	updateContact,
	updateContactPropertyMortgage,
} from '../../Agent/agent-service';
import { Redirect } from 'react-router';
import { translate } from '../../Internationalization';
import { withContactService } from '../../../service-container';
import BaseButton from '../../../shared/BaseButton/BaseButton';
import captureError from '../../../utils/captureError';
import ContactFinancesAndValuation from '../../shared/ContactFinancesAndValuation';
import ContactForm from '../../Agent/Contacts/ContactForm';
import FormErrorMessages from '../../../shared/Forms/Messages/FormErrorMessages';
import FormSuccessMessages from '../../../shared/Forms/Messages/FormSuccessMessages';
import formatCentsForDisplay from '../../../utils/formatCentsForDisplay';
import formatDateForDisplay from '../../../utils/formatDateForDisplay';
import formatDateStringForDatepicker from '../../../utils/formatDateStringForDatepicker';
import FinaceWidgets, {
	HOMEKEEPR_FINANCE_WIDGET_KEYS,
} from '../../shared/FinaceWidgets';
import {
	buildFinanceWidgetConfiguration,
	calculateEstimatedCurrentBalance,
	calculateEstimatedCurrentEquity,
	calculateFinanceWidgetMonthlyPayment,
	convertFinanceWidgetCentsToDollars,
	getLatestQuantariumValueInDollars,
} from '../../shared/FinanceWidgets/financeWidgetAttributes';
import images from '../../../config/local/images';
import LinkButton from '../../../shared/LinkButton';
import Loading from '../../../shared/Loading';
import NoContent from '../../../shared/NoContent';
import paths from '../../../config/local/paths';
import React from 'react';
import PropertyDropdownSelector from '../../shared/PropertyDropdownSelector';
import ResendInviteButton from '../../shared/ResendInviteButton';

const agentContactFinanceWidgetKeys = [
	HOMEKEEPR_FINANCE_WIDGET_KEYS.payLumpSum,
	HOMEKEEPR_FINANCE_WIDGET_KEYS.payExtra,
	HOMEKEEPR_FINANCE_WIDGET_KEYS.howMuchPaid,
	HOMEKEEPR_FINANCE_WIDGET_KEYS.buyNewHome,
	HOMEKEEPR_FINANCE_WIDGET_KEYS.cashOutRefinance,
	HOMEKEEPR_FINANCE_WIDGET_KEYS.investInHomeImprovements,
	HOMEKEEPR_FINANCE_WIDGET_KEYS.sellAndKeepProceeds,
];

/** @extends {React.Component<Props, State>} */
class EditContact extends React.Component {
	/** @type {Auth} */
	context = /** @type {Auth} */ ({});

	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			activities: [],
			activityComment: '',
			activityErrorMessage: null,
			activitySuccessMessage: '',
			agentId: '',
			availableTags: [],
			contactId: '',
			deleteWasSuccessful: false,
			editWasSuccessful: false,
			errorMessage: null,
			initialValues: {
				city: '',
				closeDate: '',
				country: '',
				createdAt: '',
				email: '',
				firstName: '',
				homeownerId: null,
				isBuyer: false,
				isHomeowner: false,
				isOwner: false,
				isSubscribedToEmail: false,
				isSubscribedToPhone: false,
				landline: '',
				languagePreference: null,
				lastName: '',
				mobile: '',
				moveDate: '',
				postalCode: '',
				properties: [],
				source: null,
				state: '',
				streetAddress1: '',
				streetAddress2: '',
				tagIds: [],
			},
			isEditing: false,
			isEditingMortgage: false,
			isGettingData: true,
			isGettingProperties: false,
			isManagingTags: false,
			isSavingActivity: false,
			isSavingMortgage: false,
			isSavingTags: false,
			mortgageDraft: null,
			mortgageErrorMessage: null,
			newTagName: '',
			resendInviteMessage: '',
			selectedPropertyId: '',
			showPopup: false,
			tagErrorMessage: null,
			tagIdsDraft: [],
			tagSuccessMessage: '',
		};

		this.handleActivityCommentChange =
			this.#handleActivityCommentChange.bind(this);
		this.handleCreateActivity = this.#handleCreateActivity.bind(this);
		this.handleClose = this.#handleClose.bind(this);
		this.handleCancelEdit = this.#handleCancelEdit.bind(this);
		this.handleConfirmDeleteContact =
			this.#handleConfirmDeleteContact.bind(this);
		this.handleDeleteContact = this.#handleDeleteContact.bind(this);
		this.handleResendInvite = this.#handleResendInvite.bind(this);
		this.handlePropertySelect = this.#handlePropertySelect.bind(this);
		this.handleSubmit = this.#handleSubmit.bind(this);
		this.handleTagDraftToggle = this.#handleTagDraftToggle.bind(this);
		this.handleTagNameChange = this.#handleTagNameChange.bind(this);
		this.handleCreateTag = this.#handleCreateTag.bind(this);
		this.handleRemoveTag = this.#handleRemoveTag.bind(this);
		this.handleSaveTags = this.#handleSaveTags.bind(this);
		this.handleToggleTagManager = this.#handleToggleTagManager.bind(this);
		this.handleToggleEdit = this.#handleToggleEdit.bind(this);
		this.handleCancelMortgageEdit =
			this.#handleCancelMortgageEdit.bind(this);
		this.handleMortgageDraftChange =
			this.#handleMortgageDraftChange.bind(this);
		this.handleSaveMortgage = this.#handleSaveMortgage.bind(this);
		this.handleStartMortgageEdit = this.#handleStartMortgageEdit.bind(this);
		this.handleContactMortgageSaved =
			this.#handleContactMortgageSaved.bind(this);
	}

	/**
	 * @returns {Promise<void>}
	 */
	async componentDidMount() {
		try {
			const contact = await getContact(
				this.props.match.params.contactId,
				this.context.accessToken
			);
			const [activities, availableTags, agentId] = await Promise.all([
				getContactActivity(
					this.props.match.params.contactId,
					this.context.accessToken
				).catch((error) => {
					captureError(error);

					return [];
				}),
				getContactTags(this.context.accessToken).catch((error) => {
					captureError(error);

					return [];
				}),
				getAgentId(this.context.accessToken).catch((error) => {
					captureError(error);

					return '';
				}),
			]);
			const properties = contact.homeownerId
				? await getContactHomeownerProperties(
						contact.homeownerId,
						this.context.accessToken
				  ).catch((error) => {
						captureError(error);

						return [];
				  })
				: [];
			const selectedProperty =
				EditContact.#getDefaultProperty(properties);

			this.setState({
				activities,
				agentId,
				availableTags,
				contactId: contact.id,
				errorMessage: null,
				initialValues: {
					city: contact.city,
					closeDate: formatDateStringForDatepicker(contact.closeDate),
					country: contact.country,
					createdAt: contact.createdAt,
					email: contact.email,
					firstName: contact.firstName,
					homeownerId: contact.homeownerId,
					isBuyer: contact.isBuyer,
					isHomeowner: !!contact.homeownerId,
					isOwner: contact.isOwner,
					isSubscribedToEmail: contact.isSubscribedToEmail,
					isSubscribedToPhone: contact.isSubscribedToPhone,
					landline: contact.landline,
					languagePreference: contact.languagePreference,
					lastName: contact.lastName,
					mobile: contact.mobile,
					moveDate: formatDateStringForDatepicker(contact.moveDate),
					postalCode: contact.postalCode,
					properties,
					source: contact.source,
					state: contact.state,
					streetAddress1: contact.streetAddress1,
					streetAddress2: contact.streetAddress2,
					tagIds: contact.tagIds,
				},
				isGettingData: false,
				selectedPropertyId: selectedProperty?.id ?? '',
				tagIdsDraft: contact.tagIds,
			});
		} catch (error) {
			captureError(error);
			this.setState({
				errorMessage: translate('global.error'),
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {string | null | undefined} homeownerId
	 * @returns {Promise<void>}
	 */
	async #setContactProperties(homeownerId) {
		if (!homeownerId) {
			return;
		}

		this.setState({ isGettingProperties: true });

		try {
			const properties = await getContactHomeownerProperties(
				homeownerId,
				this.context.accessToken
			);
			const selectedProperty =
				EditContact.#getDefaultProperty(properties);

			this.setState((prevState) => ({
				initialValues: {
					...prevState.initialValues,
					properties,
				},
				isGettingProperties: false,
				selectedPropertyId: selectedProperty?.id ?? '',
			}));
		} catch (error) {
			captureError(error);
			this.setState({
				isGettingProperties: false,
			});
		}
	}

	/**
	 * @param {ContactFormValues} values
	 * @returns {Promise<void>}
	 */
	async #handleSubmit(values) {
		try {
			/** @type {Contact} */
			const contact = {
				agentId: await getAgentId(this.context.accessToken),
				city: values.city,
				closeDate: values.closeDate,
				country: values.country ?? '',
				email: values.email,
				firstName: values.firstName,
				landline: values.landline,
				lastName: values.lastName,
				mobile: values.mobile,
				moveDate: values.moveDate,
				postalCode: values.postalCode,
				state: values.state,
				streetAddress1: values.streetAddress1,
				streetAddress2: values.streetAddress2,
			};

			await updateContact(
				this.props.match.params.contactId,
				contact,
				this.context.accessToken
			);
			this.setState({
				editWasSuccessful: true,
				errorMessage: null,
				initialValues: {
					...this.state.initialValues,
					...values,
				},
				isEditing: false,
			});
		} catch (error) {
			captureError(error);
			this.setState({
				errorMessage: translate('global.error'),
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {Event & { currentTarget: HTMLInputElement }} event
	 * @returns {Promise<void>}
	 */
	async #handleResendInvite(event) {
		event.preventDefault();
		try {
			await this.props.contactService.inviteContact(this.state.contactId);
			this.setState(
				/**
				 * @param {State} prevState
				 * @returns {{resendInviteMessage: string}}
				 */
				(prevState) => ({
					resendInviteMessage: translate(
						'agent.pages.contacts.edit.success',
						prevState.initialValues.firstName,
						prevState.initialValues.lastName
					),
				})
			);
		} catch (error) {
			captureError(error);
			this.setState({
				resendInviteMessage: translate(
					'agent.pages.contacts.edit.error'
				),
			});
		}
	}

	/**
	 * @returns {void}
	 */
	#handleDeleteContact() {
		this.setState({
			showPopup: true,
		});
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #handleConfirmDeleteContact() {
		try {
			await this.props.contactService.removeContact([
				this.state.contactId,
			]);
			this.setState({
				deleteWasSuccessful: true,
				showPopup: false,
			});
		} catch (error) {
			captureError(error);
			this.setState({
				errorMessage: translate(
					'agent.pages.contacts.edit.delete_error'
				),
				isGettingData: false,
			});
		}
	}

	/**
	 * @returns {void}
	 */
	#handleClose() {
		this.setState({ showPopup: false });
	}

	/**
	 * @returns {void}
	 */
	#handleCancelEdit() {
		this.setState({
			errorMessage: null,
			isEditing: false,
			resendInviteMessage: '',
			showPopup: false,
		});
	}

	/**
	 * @returns {void}
	 */
	#handleToggleEdit() {
		this.setState((prevState) => ({
			activityErrorMessage: null,
			activitySuccessMessage: '',
			editWasSuccessful: false,
			errorMessage: null,
			isEditing: !prevState.isEditing,
		}));
	}

	/**
	 * @param {string} propertyId
	 * @returns {void}
	 */
	#handlePropertySelect(propertyId) {
		this.setState({
			selectedPropertyId: propertyId,
		});
	}

	/**
	 * @param {string} propertyId
	 * @param {MortgageDetails} mortgageDetails
	 * @returns {Promise<void>}
	 */
	async #handleContactMortgageSaved(propertyId, mortgageDetails) {
		await this.#setContactProperties(this.state.initialValues.homeownerId);

		this.setState((prevState) => ({
			initialValues: {
				...prevState.initialValues,
				properties: prevState.initialValues.properties.map(
					(property) => {
						if (property.id !== propertyId) {
							return property;
						}

						return EditContact.#mergeMortgageDetails(
							property,
							mortgageDetails
						);
					}
				),
			},
			selectedPropertyId: propertyId,
		}));
	}

	/**
	 * @param {React.ChangeEvent<HTMLTextAreaElement>} event
	 * @returns {void}
	 */
	#handleActivityCommentChange(event) {
		this.setState({
			activityComment: event.target.value,
			activityErrorMessage: null,
			activitySuccessMessage: '',
		});
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #handleCreateActivity() {
		const description = this.state.activityComment.trim();

		if (!description) {
			this.setState({
				activityErrorMessage: translate(
					'agent.pages.contacts.edit.activity.create_required'
				),
				activitySuccessMessage: '',
			});
			return;
		}

		try {
			this.setState({
				activityErrorMessage: null,
				activitySuccessMessage: '',
				isSavingActivity: true,
			});

			await createContactActivity(
				this.state.contactId,
				description,
				this.context.accessToken
			);

			const activities = await getContactActivity(
				this.state.contactId,
				this.context.accessToken
			);

			this.setState({
				activities,
				activityComment: '',
				activityErrorMessage: null,
				activitySuccessMessage: translate(
					'agent.pages.contacts.edit.activity.create_success'
				),
				isSavingActivity: false,
			});
		} catch (error) {
			captureError(error);
			this.setState({
				activityErrorMessage: translate(
					'agent.pages.contacts.edit.activity.create_error'
				),
				activitySuccessMessage: '',
				isSavingActivity: false,
			});
		}
	}

	/**
	 * @returns {void}
	 */
	#handleToggleTagManager() {
		this.setState((prevState) => ({
			isManagingTags: !prevState.isManagingTags,
			newTagName: '',
			tagErrorMessage: null,
			tagIdsDraft: prevState.isManagingTags
				? prevState.initialValues.tagIds
				: [...prevState.initialValues.tagIds],
			tagSuccessMessage: prevState.isManagingTags
				? prevState.tagSuccessMessage
				: '',
		}));
	}

	/**
	 * @param {string} tagId
	 * @returns {void}
	 */
	#handleTagDraftToggle(tagId) {
		this.setState((prevState) => {
			const tagIdSet = new Set(prevState.tagIdsDraft);

			if (tagIdSet.has(tagId)) {
				tagIdSet.delete(tagId);
			} else {
				tagIdSet.add(tagId);
			}

			return {
				tagErrorMessage: null,
				tagIdsDraft: [...tagIdSet],
				tagSuccessMessage: '',
			};
		});
	}

	/**
	 * @param {React.ChangeEvent<HTMLInputElement>} event
	 * @returns {void}
	 */
	#handleTagNameChange(event) {
		this.setState({
			newTagName: event.target.value,
			tagErrorMessage: null,
			tagSuccessMessage: '',
		});
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #handleCreateTag() {
		const tagName = this.state.newTagName.trim();

		if (!tagName) {
			this.setState({
				tagErrorMessage: translate(
					'agent.pages.contacts.edit.tags.create_required'
				),
			});
			return;
		}

		try {
			this.setState({
				isSavingTags: true,
				tagErrorMessage: null,
				tagSuccessMessage: '',
			});

			const tag = await createContactTag(
				tagName,
				this.context.accessToken
			);
			const nextTagIds = [
				...new Set([...this.state.initialValues.tagIds, tag.id]),
			];

			await assignContactTags(
				this.state.contactId,
				nextTagIds,
				this.context.accessToken
			);

			this.setState((prevState) => ({
				availableTags: [...prevState.availableTags, tag],
				initialValues: {
					...prevState.initialValues,
					tagIds: nextTagIds,
				},
				isManagingTags: false,
				isSavingTags: false,
				newTagName: '',
				tagIdsDraft: nextTagIds,
				tagSuccessMessage: translate(
					'agent.pages.contacts.edit.tags.create_success'
				),
			}));
		} catch (error) {
			captureError(error);
			this.setState({
				isSavingTags: false,
				tagErrorMessage: translate(
					'agent.pages.contacts.edit.tags.create_error'
				),
			});
		}
	}

	/**
	 * @param {string} tagId
	 * @returns {Promise<void>}
	 */
	async #handleRemoveTag(tagId) {
		try {
			const currentTagIds = this.state.initialValues.tagIds;
			const contactId = this.state.contactId;
			const nextTagIds = currentTagIds.filter(
				(currentTagId) => currentTagId !== tagId
			);

			this.setState({
				isSavingTags: true,
				tagErrorMessage: null,
				tagSuccessMessage: '',
			});

			await assignContactTags(
				contactId,
				nextTagIds,
				this.context.accessToken
			);

			this.setState((prevState) => ({
				initialValues: {
					...prevState.initialValues,
					tagIds: nextTagIds,
				},
				isManagingTags: false,
				isSavingTags: false,
				tagIdsDraft: nextTagIds,
				tagSuccessMessage: translate(
					'agent.pages.contacts.edit.tags.save_success'
				),
			}));
		} catch (error) {
			captureError(error);
			this.setState({
				isSavingTags: false,
				tagErrorMessage: translate(
					'agent.pages.contacts.edit.tags.remove_error'
				),
			});
		}
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #handleSaveTags() {
		try {
			this.setState({
				isSavingTags: true,
				tagErrorMessage: null,
				tagSuccessMessage: '',
			});

			const tagIds = [...new Set(this.state.tagIdsDraft)];

			await assignContactTags(
				this.state.contactId,
				tagIds,
				this.context.accessToken
			);

			this.setState((prevState) => ({
				initialValues: {
					...prevState.initialValues,
					tagIds,
				},
				isManagingTags: false,
				isSavingTags: false,
				tagIdsDraft: tagIds,
				tagSuccessMessage: translate(
					'agent.pages.contacts.edit.tags.save_success'
				),
			}));
		} catch (error) {
			captureError(error);
			this.setState({
				isSavingTags: false,
				tagErrorMessage: translate(
					'agent.pages.contacts.edit.tags.save_error'
				),
			});
		}
	}

	/**
	 * @param {string | null | undefined} value
	 * @returns {string}
	 */
	static #formatValue(value) {
		return value || '—';
	}

	/**
	 * @param {State['initialValues']['properties']} properties
	 * @returns {State['initialValues']['properties'][0] | undefined}
	 */
	static #getDefaultProperty(properties) {
		return (
			properties.find((property) => property.isPrimary) ??
			properties.find(Boolean)
		);
	}

	/**
	 * @param {State['initialValues']['properties'][0]} property
	 * @returns {string}
	 */
	static #formatPropertyAddress(property) {
		const address = [
			property.address.streetAddress1,
			property.address.streetAddress2,
			property.address.city,
			property.address.state,
			property.address.postalCode,
			property.address.country,
		]
			.filter(Boolean)
			.join(', ');

		return address || property.title || 'Property';
	}

	/**
	 * @returns {State['initialValues']['properties'][0] | undefined}
	 */
	#getSelectedProperty() {
		return this.state.initialValues.properties.find(
			(property) => property.id === this.state.selectedPropertyId
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderPropertySelector() {
		const properties = this.state.initialValues.properties;

		if (!properties.length) {
			return null;
		}

		return (
			<section className="contact-property-selector">
				<PropertyDropdownSelector
					className="fancy-dropdown"
					formatPropertyLabel={(
						/** @type {State['initialValues']['properties'][0]} */ property
					) => EditContact.#formatPropertyAddress(property)}
					idPrefix="contact-property-selector"
					onSelect={this.handlePropertySelect}
					propertyList={properties}
					selectedProperty={this.#getSelectedProperty()}
				/>
			</section>
		);
	}

	/**
	 * @param {State['initialValues']['properties'][0] | undefined} property
	 * @returns {Map<string, State['initialValues']['properties'][0]['valuations']> | null}
	 */
	static #getValuationMap(property) {
		if (!property?.valuations.length) {
			return null;
		}

		const valuationMap = new Map();

		property.valuations.forEach((valuation) => {
			const provider = valuation.provider;
			const providerValuations = valuationMap.get(provider) ?? [];
			providerValuations.push(valuation);
			valuationMap.set(provider, providerValuations);
		});

		return valuationMap;
	}

	/**
	 * @param {State['initialValues']['properties'][0]['valuations']} valuations
	 * @returns {{max: number, min: number} | null}
	 */
	static #getValuationRange(valuations) {
		/** @type {number[]} */
		const range = valuations.reduce((values, valuation) => {
			if (valuation.valueLow !== null) {
				values.push(valuation.valueLow);
			}

			if (valuation.valueHigh !== null) {
				values.push(valuation.valueHigh);
			}

			return values;
		}, /** @type {number[]} */ ([]));

		if (!range.length) {
			return null;
		}

		return {
			max: Math.max(...range),
			min: Math.min(...range),
		};
	}

	/**
	 * @param {State['initialValues']['properties'][0] | undefined} selectedProperty
	 * @returns {JSX.Element}
	 */
	static #renderMarketRange(selectedProperty) {
		const range = EditContact.#getValuationRange(
			selectedProperty?.valuations ?? []
		);

		if (range === null) {
			return <NoContent />;
		}

		return (
			<section className="mt-10">
				<h2 className="h-ms-4 text-center">
					{translate('global.finance_property_value_card.header')}
				</h2>
				<div className="message bordered flex justify-around text-center mt-5">
					<section>
						<h3 className="h-ms text-grey-700 uppercase">
							{translate(
								'global.finance_property_value_card.low'
							)}
						</h3>
						<p className="h-ms-3">
							{formatCentsForDisplay(range.min, 0)}
						</p>
					</section>
					<section>
						<h3 className="h-ms text-grey-700 uppercase">
							{translate(
								'global.finance_property_value_card.high'
							)}
						</h3>
						<p className="h-ms-3">
							{formatCentsForDisplay(range.max, 0)}
						</p>
					</section>
				</div>
			</section>
		);
	}

	/**
	 * @param {number | null | undefined} value
	 * @returns {string}
	 */
	static #formatMortgageCurrency(value) {
		return value === null || value === undefined
			? ''
			: value.toLocaleString('en-US', {
					currency: 'USD',
					maximumFractionDigits: 0,
					style: 'currency',
			  });
	}

	/**
	 * @param {Date | string | null | undefined} value
	 * @returns {Date | null}
	 */
	static #normalizeMortgageDate(value) {
		if (!value) {
			return null;
		}

		const date = value instanceof Date ? value : new Date(value);

		if (Number.isNaN(date.valueOf()) || date.valueOf() === 0) {
			return null;
		}

		return date;
	}

	/**
	 * @param {Date | string | null | undefined} value
	 * @returns {string}
	 */
	static #formatMortgageDisplayDate(value) {
		const date = EditContact.#normalizeMortgageDate(value);

		return date ? date.toLocaleDateString('en-US') : '';
	}

	/**
	 * @param {Date | string | null | undefined} value
	 * @returns {string}
	 */
	static #formatMortgageDateForInput(value) {
		const date = EditContact.#normalizeMortgageDate(value);

		if (!date) {
			return '';
		}

		return [
			date.getFullYear(),
			String(date.getMonth() + 1).padStart(2, '0'),
			String(date.getDate()).padStart(2, '0'),
		].join('-');
	}

	/**
	 * @param {string} value
	 * @returns {Date | null}
	 */
	static #parseMortgageDateInput(value) {
		if (!value) {
			return null;
		}

		const date = new Date(`${value}T00:00:00`);

		return Number.isNaN(date.valueOf()) ? null : date;
	}

	/**
	 * @param {string} value
	 * @returns {number | null}
	 */
	static #parseMortgageNumberInput(value) {
		if (value === '') {
			return null;
		}

		const number = Number(value);

		return Number.isNaN(number) ? null : number;
	}

	/**
	 * @param {number | null | undefined} value
	 * @returns {string}
	 */
	static #formatMortgageNumberInput(value) {
		return value === null || value === undefined ? '' : String(value);
	}

	/**
	 * @param {number} value
	 * @returns {number}
	 */
	static #roundMortgageAmount(value) {
		return Math.round((value + Number.EPSILON) * 100) / 100;
	}

	/**
	 * @param {number | null | undefined} amount
	 * @param {number | null | undefined} monthlyAmount
	 * @param {'annual' | 'monthly'} frequency
	 * @returns {string}
	 */
	static #formatEscrowDraftAmount(amount, monthlyAmount, frequency) {
		if (amount !== null && amount !== undefined) {
			return EditContact.#formatMortgageNumberInput(amount);
		}

		if (monthlyAmount === null || monthlyAmount === undefined) {
			return '';
		}

		const displayAmount =
			frequency === 'annual' ? monthlyAmount * 12 : monthlyAmount;

		return EditContact.#formatMortgageNumberInput(
			EditContact.#roundMortgageAmount(displayAmount)
		);
	}

	/**
	 * @param {number | null | undefined} value
	 * @returns {number}
	 */
	static #getMortgageMonthlyAmount(value) {
		return value ?? 0;
	}

	/**
	 * @param {number | null | undefined} value
	 * @returns {'annual' | 'monthly'}
	 */
	static #getDefaultEscrowFrequency(value) {
		void value;

		return 'monthly';
	}

	/**
	 * @param {number | null | undefined} value
	 * @param {'annual' | 'monthly'} frequency
	 * @returns {number | null}
	 */
	static #normalizeEscrowMonthlyAmount(value, frequency) {
		if (value === null || value === undefined) {
			return null;
		}

		const monthlyValue = frequency === 'annual' ? value / 12 : value;

		return EditContact.#roundMortgageAmount(monthlyValue);
	}

	/**
	 * @param {Date | null} value
	 * @returns {string | null}
	 */
	static #formatMortgageDateForSaving(value) {
		return value ? EditContact.#formatMortgageDateForInput(value) : null;
	}

	/**
	 * @param {State['initialValues']['properties'][0]} property
	 * @returns {NonNullable<State['mortgageDraft']>}
	 */
	static #createMortgageDraft(property) {
		const details = property.details;
		const escrowInsuranceFrequency =
			details.escrowInsuranceFrequency ??
			EditContact.#getDefaultEscrowFrequency(
				details.escrowInsuranceMonthly
			);
		const escrowTaxFrequency =
			details.escrowTaxFrequency ??
			EditContact.#getDefaultEscrowFrequency(details.escrowTaxMonthly);

		return {
			amortizationYears: EditContact.#formatMortgageNumberInput(
				details.amortizationYears ?? 30
			),
			amountToLoan: EditContact.#formatMortgageNumberInput(
				details.amountToLoan
			),
			escrowInsurance: EditContact.#formatEscrowDraftAmount(
				details.escrowInsuranceAmount,
				details.escrowInsuranceMonthly,
				escrowInsuranceFrequency
			),
			escrowInsuranceFrequency,
			escrowTax: EditContact.#formatEscrowDraftAmount(
				details.escrowTaxAmount,
				details.escrowTaxMonthly,
				escrowTaxFrequency
			),
			escrowTaxFrequency,
			originalDateOfContract: EditContact.#formatMortgageDateForInput(
				details.originalDateOfContract
			),
			rate: EditContact.#formatMortgageNumberInput(details.rate),
		};
	}

	/**
	 * @param {NonNullable<State['mortgageDraft']>} draft
	 * @returns {MortgageDetails}
	 */
	static #mapMortgageDraftToDetails(draft) {
		const escrowInsurance = EditContact.#parseMortgageNumberInput(
			draft.escrowInsurance
		);
		const escrowTax = EditContact.#parseMortgageNumberInput(
			draft.escrowTax
		);

		return {
			amortizationYears: EditContact.#parseMortgageNumberInput(
				draft.amortizationYears
			),
			amountToLoan: EditContact.#parseMortgageNumberInput(
				draft.amountToLoan
			),
			escrowInsuranceAmount:
				escrowInsurance === null
					? null
					: EditContact.#roundMortgageAmount(escrowInsurance),
			escrowInsuranceFrequency: draft.escrowInsuranceFrequency,
			escrowInsuranceMonthly: EditContact.#normalizeEscrowMonthlyAmount(
				escrowInsurance,
				draft.escrowInsuranceFrequency
			),
			escrowTaxAmount:
				escrowTax === null
					? null
					: EditContact.#roundMortgageAmount(escrowTax),
			escrowTaxFrequency: draft.escrowTaxFrequency,
			escrowTaxMonthly: EditContact.#normalizeEscrowMonthlyAmount(
				escrowTax,
				draft.escrowTaxFrequency
			),
			originalDateOfContract: EditContact.#parseMortgageDateInput(
				draft.originalDateOfContract
			),
			rate: EditContact.#parseMortgageNumberInput(draft.rate),
		};
	}

	/**
	 * @param {State['initialValues']['properties'][0]} property
	 * @param {MortgageDetails} details
	 * @returns {State['initialValues']['properties'][0]}
	 */
	static #mergeMortgageDetails(property, details) {
		return {
			...property,
			details: {
				...property.details,
				...details,
			},
		};
	}

	/**
	 * @returns {void}
	 */
	#handleStartMortgageEdit() {
		const selectedProperty = this.#getSelectedProperty();

		if (!selectedProperty) {
			return;
		}

		this.setState({
			isEditingMortgage: true,
			mortgageDraft: EditContact.#createMortgageDraft(selectedProperty),
			mortgageErrorMessage: null,
		});
	}

	/**
	 * @returns {void}
	 */
	#handleCancelMortgageEdit() {
		this.setState({
			isEditingMortgage: false,
			mortgageDraft: null,
			mortgageErrorMessage: null,
		});
	}

	/**
	 * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} event
	 * @returns {void}
	 */
	#handleMortgageDraftChange(event) {
		const { name, value } = event.currentTarget;

		this.setState((prevState) => ({
			mortgageDraft: prevState.mortgageDraft
				? {
						...prevState.mortgageDraft,
						[name]: value,
				  }
				: null,
		}));
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #handleSaveMortgage() {
		const selectedProperty = this.#getSelectedProperty();
		const draft = this.state.mortgageDraft;

		if (!selectedProperty || !draft) {
			return;
		}

		const mortgageDetails = EditContact.#mapMortgageDraftToDetails(draft);

		this.setState({
			isSavingMortgage: true,
			mortgageErrorMessage: null,
		});

		try {
			await updateContactPropertyMortgage(
				this.props.match.params.contactId,
				selectedProperty.id,
				{
					...mortgageDetails,
					originalDateOfContract:
						EditContact.#formatMortgageDateForSaving(
							mortgageDetails.originalDateOfContract
						),
				},
				this.context.accessToken
			);

			await this.#setContactProperties(
				this.state.initialValues.homeownerId
			);

			this.setState((prevState) => ({
				initialValues: {
					...prevState.initialValues,
					properties: prevState.initialValues.properties.map(
						(property) => {
							if (property.id !== selectedProperty.id) {
								return property;
							}

							return EditContact.#mergeMortgageDetails(
								property,
								mortgageDetails
							);
						}
					),
				},
				isEditingMortgage: false,
				isSavingMortgage: false,
				mortgageDraft: null,
			}));
		} catch (error) {
			captureError(error);
			this.setState({
				isSavingMortgage: false,
				mortgageErrorMessage: translate('global.error'),
			});
		}
	}

	/**
	 * @param {State['initialValues']['properties'][0] | undefined} selectedProperty
	 * @returns {number | null}
	 */
	static #getAverageValuationValue(selectedProperty) {
		const valuationRange = EditContact.#getValuationRange(
			selectedProperty?.valuations ?? []
		);

		if (valuationRange !== null) {
			return convertFinanceWidgetCentsToDollars(
				(valuationRange.min + valuationRange.max) / 2
			);
		}

		return convertFinanceWidgetCentsToDollars(
			selectedProperty?.valuations[0]?.valueMean
		);
	}

	/**
	 * @param {State['initialValues']['properties'][0] | undefined} selectedProperty
	 * @returns {JSX.Element | null}
	 */
	#renderFinanceWidgets(selectedProperty) {
		if (!selectedProperty) {
			return null;
		}

		const details = selectedProperty.details;
		const estimatedHomeValue =
			EditContact.#getAverageValuationValue(selectedProperty);
		const configuration = buildFinanceWidgetConfiguration({
			accessToken: this.context.accessToken,
			agentId: this.state.agentId,
			details,
			estimatedHomeValue,
			userId: this.context.userId,
			userType: 'agent',
		});

		return (
			<FinaceWidgets
				attributes={configuration.attributes}
				reloadKey={`${selectedProperty.id}:${configuration.reloadKey}`}
				title="Finance Tools"
				widgetKeys={agentContactFinanceWidgetKeys}
			/>
		);
	}

	/**
	 * @param {string} header
	 * @param {string | null | undefined} footer
	 * @param {boolean} [isFullWidth]
	 * @returns {JSX.Element}
	 */
	static #renderMortgageItem(header, footer, isFullWidth = false) {
		return (
			<div
				className={`property-detail-item ${
					isFullWidth ? 'width-full' : ''
				}`}
				key={header}
			>
				<dt className="property-detail-item-header">{header}</dt>
				<dd className="property-detail-item-footer">
					{footer ||
						translate(
							'homeowner.pages.dashboard.properties.missing_datum'
						)}
				</dd>
			</div>
		);
	}

	/**
	 * @param {string} header
	 * @param {string} name
	 * @param {string} value
	 * @param {'date' | 'number'} type
	 * @param {string} [suffix]
	 * @returns {JSX.Element}
	 */
	#renderMortgageFormField(header, name, value, type, suffix = '') {
		return (
			<div className="mortgage-form-field min-w-0" key={header}>
				<label
					className="mortgage-form-label block h-ms mb-2 text-fg"
					htmlFor={name}
				>
					{header}
				</label>
				<div className="mortgage-form-control flex items-center gap-2 min-w-0">
					<input
						className="input mortgage-form-input min-w-0 w-full"
						id={name}
						inputMode={type === 'number' ? 'decimal' : undefined}
						min={type === 'number' ? '0' : undefined}
						name={name}
						onChange={this.handleMortgageDraftChange}
						step={type === 'number' ? '0.01' : undefined}
						type={type === 'number' ? 'text' : type}
						value={value}
					/>
					{suffix ? (
						<span className="mortgage-form-suffix hl-ms whitespace-nowrap">
							{suffix}
						</span>
					) : null}
				</div>
			</div>
		);
	}

	/**
	 * @param {string} header
	 * @param {string} name
	 * @param {string} value
	 * @param {'annual' | 'monthly'} frequency
	 * @returns {JSX.Element}
	 */
	#renderMortgageEscrowField(header, name, value, frequency) {
		const frequencyName = `${name}Frequency`;

		return (
			<div
				className="mortgage-form-field mortgage-form-field-compact min-w-0"
				key={header}
			>
				<label
					className="mortgage-form-label block h-ms mb-2 text-fg"
					htmlFor={name}
				>
					{header}
				</label>
				<div className="mortgage-form-control flex items-center gap-2 min-w-0">
					<input
						className="input mortgage-form-input min-w-0 w-full"
						id={name}
						inputMode="decimal"
						min="0"
						name={name}
						onChange={this.handleMortgageDraftChange}
						step="0.01"
						type="text"
						value={value}
					/>
					<select
						className="input mortgage-form-select min-w-0 w-36"
						name={frequencyName}
						onChange={this.handleMortgageDraftChange}
						value={frequency}
					>
						<option value="monthly">Monthly</option>
						<option value="annual">Annual</option>
					</select>
				</div>
			</div>
		);
	}

	/**
	 * @param {string} header
	 * @param {string | null | undefined} value
	 * @returns {JSX.Element}
	 */
	static #renderMortgageDisplayField(header, value) {
		return (
			<div className="mortgage-form-field min-w-0" key={header}>
				<div className="mortgage-form-label block h-ms mb-2 text-fg">
					{header}
				</div>
				<div className="mortgage-form-readonly min-h-10 border border-grey-100 rounded px-3 py-2 hl-ms">
					{value ||
						translate(
							'homeowner.pages.dashboard.properties.missing_datum'
						)}
				</div>
			</div>
		);
	}

	/**
	 * @param {NonNullable<State['mortgageDraft']>} draft
	 * @param {{amortizationPeriod: string, amountToLoan: string, escrowInsurance: string, escrowTax: string, estimatedPrincipalInterest: string, interestRate: string, originalDateOfContract: string, totalPayment: string, years: string}} labels
	 * @param {string | null} estimatedPrincipalAndInterest
	 * @param {string | null} totalPayment
	 * @returns {JSX.Element}
	 */
	#renderMortgageEditForm(
		draft,
		labels,
		estimatedPrincipalAndInterest,
		totalPayment
	) {
		return (
			<div className="card-body mortgage-edit-form grid gap-x-8 gap-y-5 text-left md:grid-cols-2">
				{this.#renderMortgageFormField(
					labels.amountToLoan,
					'amountToLoan',
					draft.amountToLoan,
					'number'
				)}
				{this.#renderMortgageFormField(
					labels.originalDateOfContract,
					'originalDateOfContract',
					draft.originalDateOfContract,
					'date'
				)}
				{this.#renderMortgageFormField(
					labels.interestRate,
					'rate',
					draft.rate,
					'number',
					'%'
				)}
				{this.#renderMortgageFormField(
					labels.amortizationPeriod,
					'amortizationYears',
					draft.amortizationYears,
					'number',
					labels.years
				)}
				{EditContact.#renderMortgageDisplayField(
					labels.estimatedPrincipalInterest,
					estimatedPrincipalAndInterest
				)}
				{this.#renderMortgageEscrowField(
					labels.escrowTax,
					'escrowTax',
					draft.escrowTax,
					draft.escrowTaxFrequency
				)}
				{this.#renderMortgageEscrowField(
					labels.escrowInsurance,
					'escrowInsurance',
					draft.escrowInsurance,
					draft.escrowInsuranceFrequency
				)}
				{EditContact.#renderMortgageDisplayField(
					labels.totalPayment,
					totalPayment
				)}
			</div>
		);
	}

	/**
	 * @param {State['initialValues']['properties'][0] | undefined} selectedProperty
	 * @returns {JSX.Element | null}
	 */
	#renderMortgageInformation(selectedProperty) {
		if (!selectedProperty) {
			return null;
		}

		const details = selectedProperty.details;
		const draft = this.state.mortgageDraft;
		const mortgageDetails =
			this.state.isEditingMortgage && draft
				? {
						...details,
						...EditContact.#mapMortgageDraftToDetails(draft),
				  }
				: details;
		const amortizationYears = mortgageDetails.amortizationYears ?? 30;
		const estimatedPrincipalAndInterest =
			calculateFinanceWidgetMonthlyPayment(
				mortgageDetails.amountToLoan,
				mortgageDetails.rate,
				amortizationYears
			);
		const totalPayment =
			estimatedPrincipalAndInterest === null
				? null
				: estimatedPrincipalAndInterest +
				  EditContact.#getMortgageMonthlyAmount(
						mortgageDetails.escrowTaxMonthly
				  ) +
				  EditContact.#getMortgageMonthlyAmount(
						mortgageDetails.escrowInsuranceMonthly
				  );
		const estimatedCurrentBalance =
			calculateEstimatedCurrentBalance(mortgageDetails);
		const estimatedCurrentEquity = calculateEstimatedCurrentEquity(
			getLatestQuantariumValueInDollars(
				EditContact.#getValuationMap(selectedProperty)
			),
			estimatedCurrentBalance
		);
		const labels = {
			amortizationPeriod: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.amortization_period'
			),
			escrowInsurance: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.escrow_insurance'
			),
			escrowTax: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.escrow_tax'
			),
			estimatedCurrentBalance: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.estimated_current_balance'
			),
			estimatedCurrentEquity: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.estimated_current_equity'
			),
			estimatedPrincipalInterest: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.estimated_principal_interest'
			),
			interestRate: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.interest_rate'
			),
			originalAmount: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.original_amount'
			),
			originationDate: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.origination_date'
			),
			totalPayment: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.total_payment'
			),
			years: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.years'
			),
		};
		const editLabels = {
			...labels,
			amountToLoan: labels.originalAmount,
			originalDateOfContract: labels.originationDate,
		};
		const readOnlyItems = [
			{
				footer: EditContact.#formatMortgageCurrency(
					mortgageDetails.amountToLoan
				),
				header: labels.originalAmount,
			},
			{
				footer: EditContact.#formatMortgageDisplayDate(
					mortgageDetails.originalDateOfContract
				),
				header: labels.originationDate,
			},
			{
				footer: EditContact.#formatMortgageCurrency(
					estimatedCurrentBalance
				),
				header: labels.estimatedCurrentBalance,
			},
			{
				footer:
					mortgageDetails.rate === null
						? null
						: `${mortgageDetails.rate}%`,
				header: labels.interestRate,
			},
			{
				footer: EditContact.#formatMortgageCurrency(
					estimatedCurrentEquity
				),
				header: labels.estimatedCurrentEquity,
			},
			{
				footer: `${amortizationYears} ${labels.years}`,
				header: labels.amortizationPeriod,
			},
		];
		const items = [
			...readOnlyItems,
			{
				footer: EditContact.#formatMortgageCurrency(
					estimatedPrincipalAndInterest
				),
				header: labels.estimatedPrincipalInterest,
			},
			{
				footer: EditContact.#formatMortgageCurrency(
					mortgageDetails.escrowTaxMonthly
				),
				header: labels.escrowTax,
			},
			{
				footer: EditContact.#formatMortgageCurrency(
					mortgageDetails.escrowInsuranceMonthly
				),
				header: labels.escrowInsurance,
			},
			{
				footer: EditContact.#formatMortgageCurrency(totalPayment),
				header: labels.totalPayment,
			},
		];

		return (
			<section className="mt-10">
				<header className="mb-3 flex items-center justify-between gap-3">
					<h2 className="h-ms-4">
						{translate(
							'homeowner.pages.finances.view.mortgage.title'
						)}
					</h2>
					{this.state.isEditingMortgage ? (
						<div className="flex gap-2">
							<BaseButton
								disabled={this.state.isSavingMortgage}
								onClick={this.handleSaveMortgage}
								size="small"
							>
								{this.state.isSavingMortgage
									? 'Saving...'
									: translate(
											'global.forms.buttons.save_text'
									  )}
							</BaseButton>
							<BaseButton
								disabled={this.state.isSavingMortgage}
								onClick={this.handleCancelMortgageEdit}
								outline={true}
								size="small"
							>
								{translate('global.forms.buttons.cancel')}
							</BaseButton>
						</div>
					) : (
						<BaseButton
							onClick={this.handleStartMortgageEdit}
							size="small"
						>
							{translate(
								'homeowner.pages.finances.view.mortgage.edit_details'
							)}
						</BaseButton>
					)}
				</header>
				{this.state.mortgageErrorMessage ? (
					<FormErrorMessages
						messages={this.state.mortgageErrorMessage}
					/>
				) : null}
				<section className="card card-property-detail card-border">
					{this.state.isEditingMortgage && draft ? (
						this.#renderMortgageEditForm(
							draft,
							editLabels,
							EditContact.#formatMortgageCurrency(
								estimatedPrincipalAndInterest
							),
							EditContact.#formatMortgageCurrency(totalPayment)
						)
					) : (
						<dl className="card-body property-details grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-start">
							{items.map((item) =>
								EditContact.#renderMortgageItem(
									item.header,
									item.footer
								)
							)}
						</dl>
					)}
				</section>
			</section>
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderFinancesAndValuation() {
		if (!this.state.initialValues.homeownerId) {
			return null;
		}

		return (
			<ContactFinancesAndValuation
				accessToken={this.context.accessToken}
				agentId={this.state.agentId}
				contactId={this.props.match.params.contactId}
				homeownerId={this.state.initialValues.homeownerId}
				isGettingProperties={this.state.isGettingProperties}
				onMortgageSaved={this.handleContactMortgageSaved}
				onPropertySelect={this.handlePropertySelect}
				properties={this.state.initialValues.properties}
				selectedPropertyId={this.state.selectedPropertyId}
				userId={this.context.userId}
			/>
		);
	}

	/**
	 * @param {string[] | undefined} tagIds
	 * @returns {{ id: string; isDefault: boolean; name: string }[]}
	 */
	#getSelectedTags(tagIds = this.state.initialValues.tagIds) {
		const selectedTagIdSet = new Set(tagIds);

		return this.state.availableTags.filter((tag) =>
			selectedTagIdSet.has(tag.id)
		);
	}

	/**
	 * @param {string | null | undefined} date
	 * @returns {string}
	 */
	static #getDisplayDate(date) {
		return date ? formatDateForDisplay(date) : '';
	}

	/**
	 * @param {string | null | undefined} value
	 * @returns {string}
	 */
	#formatActivityTimestamp(value) {
		return value ? formatDateForDisplay(value) : '—';
	}

	/**
	 * @param {{ isSystemGenerated: boolean }} activity
	 * @returns {string}
	 */
	#getActivitySourceBadgeClass(activity) {
		return activity.isSystemGenerated
			? 'inline-flex items-center rounded-full border border-theme px-3 py-1 text-xs font-subheader font-bold text-theme'
			: 'inline-flex items-center rounded-full border border-grey-300 px-3 py-1 text-xs font-subheader font-bold text-grey-700';
	}

	/**
	 * @returns {JSX.Element}
	 */
	#renderActivity() {
		return (
			<section className="h-full">
				<header className="flex items-center justify-between gap-4 mb-4">
					<h2 className="h-ms-5">
						{translate('agent.pages.contacts.edit.activity.title')}
					</h2>
				</header>

				<FormSuccessMessages
					className="mb-4"
					messages={this.state.activitySuccessMessage}
				/>
				<FormErrorMessages
					className="mb-4"
					messages={[this.state.activityErrorMessage]}
				/>

				<section className="border border-theme rounded p-4 mb-6">
					<section className="input-group mb-4">
						<label
							className="input-label"
							htmlFor="contact-activity-comment"
						>
							{translate(
								'agent.pages.contacts.edit.activity.create_label'
							)}
						</label>
						<textarea
							className="input min-h-32"
							id="contact-activity-comment"
							onChange={this.handleActivityCommentChange}
							placeholder={translate(
								'agent.pages.contacts.edit.activity.create_placeholder'
							)}
							value={this.state.activityComment}
						/>
					</section>

					<footer className="button-group">
						<BaseButton
							disabled={
								this.state.isSavingActivity ||
								!this.state.activityComment.trim()
							}
							onClick={this.handleCreateActivity}
							type="button"
						>
							{this.state.isSavingActivity
								? translate(
										'agent.pages.contacts.edit.activity.saving'
								  )
								: translate(
										'agent.pages.contacts.edit.activity.create'
								  )}
						</BaseButton>
					</footer>
				</section>

				{this.state.activities.length ? (
					<section className="border-l-2 border-theme pl-4 space-y-3">
						{this.state.activities.map((activity) => (
							<article
								className="text-sm leading-6"
								key={activity.id}
							>
								<div className="text-sm">
									{this.#formatActivityTimestamp(
										activity.createdAt
									)}{' '}
									<span className="font-subheader font-bold">
										({activity.sourceLabel})
									</span>
									{' - '}
									<span>{activity.description}</span>
								</div>
							</article>
						))}
					</section>
				) : (
					<input
						className="input input-disabled"
						disabled={true}
						readOnly={true}
						type="text"
						value={translate(
							'agent.pages.contacts.edit.activity.empty'
						)}
					/>
				)}
			</section>
		);
	}

	/**
	 * @param {string} label
	 * @param {string | null | undefined} value
	 * @param {string} [className]
	 * @returns {JSX.Element}
	 */
	static #renderReadOnlyField(label, value, className = '') {
		return (
			<section className={className || ''}>
				<label className="input-label">{label}</label>
				<div className="min-h-[3.2rem] py-1.5 text-ms-0 leading-normal text-grey-900">
					{EditContact.#formatValue(value)}
				</div>
			</section>
		);
	}

	/**
	 * @returns {JSX.Element}
	 */
	#renderDetails() {
		const contact = this.state.initialValues;
		const referrer =
			this.props.location?.state?.referrer ??
			paths.app.agent.contacts.root;
		const fullName =
			[contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
			contact.email;
		const deleteContactButton = this.#renderDeleteContactButton(
			contact.isHomeowner
		);
		const deletePopup = this.#renderDeletePopup(fullName);
		const draftSelectedTags = this.#getSelectedTags(this.state.tagIdsDraft);
		const selectedTags = this.#getSelectedTags();
		const successMessage = this.#renderEditSuccessMessage();

		return (
			<React.Fragment>
				{deletePopup}

				<header className="flex items-center justify-between mt-10 mb-6">
					<h1 className="hl-ms-6">
						{translate('admin.pages.contacts.edit.title')}
					</h1>

					<section className="button-group h-fit">
						<BaseButton
							onClick={this.handleToggleEdit}
							outline={true}
							type="button"
						>
							{translate('global.forms.buttons.edit')}
						</BaseButton>
						<LinkButton icon={images.icons.delete} to={referrer}>
							{translate('agent.pages.contacts.form.cancel')}
						</LinkButton>
					</section>
				</header>

				{successMessage}

				<fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
					<legend className="h-ms-5 mb-6">
						{translate('admin.pages.contacts.form.contact_title')}
					</legend>
					{EditContact.#renderReadOnlyField(
						'First Name',
						contact.firstName
					)}
					{EditContact.#renderReadOnlyField(
						'Last Name',
						contact.lastName
					)}
					{EditContact.#renderReadOnlyField(
						translate('global.forms.inputs.email.label'),
						contact.email,
						'col-span-full input-group'
					)}
					{EditContact.#renderReadOnlyField(
						'Landline Phone',
						contact.landline
					)}
					{EditContact.#renderReadOnlyField(
						'Mobile Phone',
						contact.mobile
					)}
					{EditContact.#renderReadOnlyField(
						'Street Address',
						contact.streetAddress1
					)}
					{EditContact.#renderReadOnlyField(
						'Address Designation',
						contact.streetAddress2
					)}
					{EditContact.#renderReadOnlyField('City', contact.city)}
					{EditContact.#renderReadOnlyField('State', contact.state)}
					{EditContact.#renderReadOnlyField(
						'Postal Code',
						contact.postalCode
					)}
					{EditContact.#renderReadOnlyField(
						'Country',
						contact.country
					)}
				</fieldset>

				<fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
					<legend className="h-ms-5 mb-6">
						{translate('agent.pages.contacts.form.moving_title')}
					</legend>

					{EditContact.#renderReadOnlyField(
						translate('agent.pages.contacts.form.move_date.label'),
						EditContact.#getDisplayDate(contact.moveDate)
					)}

					{EditContact.#renderReadOnlyField(
						translate('agent.pages.contacts.form.close_date.label'),
						EditContact.#getDisplayDate(contact.closeDate)
					)}
				</fieldset>

				<section className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 items-start">
					<section>
						<header className="flex items-center justify-between gap-4 mb-4">
							<h2 className="h-ms-5">
								{translate(
									'agent.pages.contacts.edit.tags.title'
								)}
							</h2>
							<BaseButton
								disabled={this.state.isSavingTags}
								onClick={this.handleToggleTagManager}
								outline={true}
								type="button"
							>
								{this.state.isManagingTags
									? translate(
											'agent.pages.contacts.edit.tags.done'
									  )
									: translate(
											'agent.pages.contacts.edit.tags.manage'
									  )}
							</BaseButton>
						</header>

						{!!this.state.tagSuccessMessage && (
							<FormSuccessMessages
								className="mb-4"
								messages={this.state.tagSuccessMessage}
							/>
						)}
						<FormErrorMessages
							className="mb-4"
							messages={[this.state.tagErrorMessage]}
						/>

						<div className="input-group">
							{selectedTags.length ? (
								<div className="flex flex-wrap gap-3">
									{selectedTags.map((tag) => (
										<span
											className="inline-flex items-center rounded-full border border-theme px-3 py-1 text-theme font-subheader font-bold"
											key={tag.id}
										>
											<span>{tag.name}</span>
											<BaseButton
												className="ml-2 h-auto min-h-0 w-auto min-w-0 border-0 bg-transparent px-0 py-0 text-theme shadow-none"
												disabled={
													this.state.isSavingTags
												}
												onClick={() =>
													this.handleRemoveTag(tag.id)
												}
												type="button"
											>
												x
											</BaseButton>
										</span>
									))}
								</div>
							) : (
								<input
									className="input input-disabled"
									disabled={true}
									readOnly={true}
									type="text"
									value="—"
								/>
							)}
						</div>

						{this.state.isManagingTags ? (
							<section className="mt-6 border border-theme p-4 rounded">
								<h3 className="font-subheader font-bold mb-3">
									{translate(
										'agent.pages.contacts.edit.tags.assign'
									)}
								</h3>

								{!!this.state.availableTags.length && (
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
										{this.state.availableTags.map((tag) => (
											<label
												className="flex items-center gap-3"
												htmlFor={`contact-tag-${tag.id}`}
												key={tag.id}
											>
												<input
													checked={this.state.tagIdsDraft.includes(
														tag.id
													)}
													id={`contact-tag-${tag.id}`}
													onChange={() =>
														this.handleTagDraftToggle(
															tag.id
														)
													}
													type="checkbox"
												/>
												<span>
													{tag.name}
													{tag.isDefault
														? ' (Default)'
														: ''}
												</span>
											</label>
										))}
									</div>
								)}

								{!!draftSelectedTags.length && (
									<div className="mb-4 text-sm text-grey-700">
										{translate(
											'agent.pages.contacts.edit.tags.selected'
										)}
										:{' '}
										{draftSelectedTags
											.map((tag) => tag.name)
											.join(', ')}
									</div>
								)}

								<section className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-3 mb-4 items-end">
									<section className="input-group mb-0">
										<label className="input-label">
											{translate(
												'agent.pages.contacts.edit.tags.create_custom'
											)}
										</label>
										<input
											className="input"
											onChange={this.handleTagNameChange}
											placeholder={translate(
												'agent.pages.contacts.edit.tags.create_placeholder'
											)}
											type="text"
											value={this.state.newTagName}
										/>
									</section>
									<BaseButton
										disabled={
											this.state.isSavingTags ||
											!this.state.newTagName.trim()
										}
										onClick={this.handleCreateTag}
										type="button"
									>
										{translate(
											'agent.pages.contacts.edit.tags.create'
										)}
									</BaseButton>
								</section>

								<footer className="button-group">
									<BaseButton
										disabled={this.state.isSavingTags}
										onClick={this.handleSaveTags}
										type="button"
									>
										{this.state.isSavingTags
											? translate(
													'agent.pages.contacts.edit.tags.saving'
											  )
											: translate(
													'agent.pages.contacts.edit.tags.save'
											  )}
									</BaseButton>
									<BaseButton
										disabled={this.state.isSavingTags}
										onClick={this.handleToggleTagManager}
										outline={true}
										type="button"
									>
										Cancel
									</BaseButton>
								</footer>
							</section>
						) : null}
					</section>

					{this.#renderActivity()}
				</section>

				<section className="button-group">
					<ResendInviteButton
						icon={images.icons.plus}
						onResendInvite={this.handleResendInvite}
						outline={true}
						size="large"
					/>
					{deleteContactButton}
				</section>

				<FormSuccessMessages
					messages={this.state.resendInviteMessage}
				/>
				<FormErrorMessages messages={[this.state.errorMessage]} />

				{this.#renderFinancesAndValuation()}
			</React.Fragment>
		);
	}

	/**
	 * @returns {string}
	 */
	#getReferrer() {
		return (
			this.props.location?.state?.referrer ??
			paths.app.agent.contacts.root
		);
	}

	/**
	 * @param {string} contactName
	 * @returns {JSX.Element | null}
	 */
	#renderDeletePopup(contactName) {
		if (!this.state.showPopup) {
			return null;
		}

		return (
			<ContactDeletePopup
				contactName={contactName}
				onClose={this.handleClose}
				onSubmit={this.handleConfirmDeleteContact}
				selectedContacts={[this.state.contactId]}
			/>
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderEditSuccessMessage() {
		if (!this.state.editWasSuccessful) {
			return null;
		}

		return (
			<FormSuccessMessages
				messages={translate('agent.pages.contacts.add.edit_message')}
			/>
		);
	}

	/**
	 * @param {boolean} isHomeowner
	 * @returns {JSX.Element | null}
	 */
	#renderDeleteContactButton(isHomeowner) {
		if (isHomeowner) {
			return null;
		}

		return (
			<BaseButton
				onClick={this.handleDeleteContact}
				outline={true}
				size="large"
				type="button"
			>
				{translate('agent.pages.contacts.view.table.delete_contact')}
			</BaseButton>
		);
	}

	/**
	 * @returns {JSX.Element}
	 */
	render() {
		if (this.state.deleteWasSuccessful) {
			return (
				<Redirect
					to={{
						pathname: paths.app.agent.contacts.root,
						state: {
							message: translate(
								'agent.pages.contacts.view.delete_contact_success'
							),
						},
					}}
				/>
			);
		}

		if (this.state.isGettingData) {
			return <Loading />;
		}

		if (!this.state.isEditing) {
			return this.#renderDetails();
		}

		/** @type {ContactFormValues} */
		const formInitialValues = {
			city: this.state.initialValues.city,
			closeDate: this.state.initialValues.closeDate,
			country: this.state.initialValues.country,
			email: this.state.initialValues.email,
			firstName: this.state.initialValues.firstName,
			landline: this.state.initialValues.landline,
			lastName: this.state.initialValues.lastName,
			mobile: this.state.initialValues.mobile,
			moveDate: this.state.initialValues.moveDate,
			postalCode: this.state.initialValues.postalCode,
			state: this.state.initialValues.state,
			streetAddress1: this.state.initialValues.streetAddress1,
			streetAddress2: this.state.initialValues.streetAddress2,
		};

		return (
			<React.Fragment>
				{!!this.state.showPopup && (
					<ContactDeletePopup
						contactName={`${this.state.initialValues.firstName} ${this.state.initialValues.lastName}`}
						onClose={this.handleClose}
						onSubmit={this.handleConfirmDeleteContact}
						selectedContacts={[this.state.contactId]}
					/>
				)}

				<ContactForm
					additionalButtons={[
						<ResendInviteButton
							icon={images.icons.plus}
							key={0}
							onResendInvite={this.handleResendInvite}
							outline={true}
							size="large"
						/>,
						!this.state.initialValues.isHomeowner && (
							<BaseButton
								key={1}
								onClick={this.handleDeleteContact}
								outline={true}
								size="large"
							>
								{translate(
									'agent.pages.contacts.view.table.delete_contact'
								)}
							</BaseButton>
						),
					]}
					editText={translate(
						'agent.pages.contacts.edit.invite.update'
					)}
					error={this.state.errorMessage}
					formTitle="Edit Contact Information"
					initialValues={formInitialValues}
					onCancel={this.handleCancelEdit}
					onSubmit={this.handleSubmit}
					resendInviteMessage={this.state.resendInviteMessage}
				/>

				{this.#renderFinancesAndValuation()}
			</React.Fragment>
		);
	}
}

EditContact.contextType = AuthContext;

export default withContactService(EditContact);
